import { z } from "zod"
import { MAX_GESTATION_WEEKS, MIN_GESTATION_WEEKS } from "@/models/common"
import { PregnancyStatus } from "@/modules/patients/services/__generated__/GynaecologyFragment.graphql"

const isValidYear = (year: string): boolean => {
  const currentYear = new Date().getFullYear()
  const parsedYear = parseInt(year, 10)
  return !isNaN(parsedYear) && parsedYear >= 1900 && parsedYear <= currentYear
}

export const gynaecologySchema = z
  .object({
    firstPeriod: z.string().optional(),
    menopauseSince: z.string().optional(),
    pregnancyStatus: z.custom<PregnancyStatus>(),
    weeksOfPregnancy: z.string().optional(),
    pregnancyHistory: z.string().optional(),
    patientBirthDate: z.string().optional(),
  })
  .refine((schema) => !(schema.pregnancyStatus === "PREGNANT" && !schema.weeksOfPregnancy), {
    message: "common.fieldErrorList.requiredField",
    path: ["weeksOfPregnancy"],
  })
  .refine(
    (schema) => {
      if (schema.weeksOfPregnancy) {
        const weeksOfPregnancy = parseInt(schema.weeksOfPregnancy)
        return !(
          schema.weeksOfPregnancy &&
          schema.pregnancyStatus === "PREGNANT" &&
          (weeksOfPregnancy > MAX_GESTATION_WEEKS || weeksOfPregnancy < MIN_GESTATION_WEEKS)
        )
      }
      return true
    },
    {
      message: "patient.healthInformation.gynaecology.form.validationMessage.validWeekPregnant",
      path: ["weeksOfPregnancy"],
    },
  )
  .refine((schema) => (schema.menopauseSince ? isValidYear(schema.menopauseSince) : true), {
    message: "patient.healthInformation.gynaecology.form.validationMessage.validYear",
    path: ["menopauseSince"],
  })
  .refine((schema) => (schema.firstPeriod ? isValidYear(schema.firstPeriod) : true), {
    message: "patient.healthInformation.gynaecology.form.validationMessage.validYear",
    path: ["firstPeriod"],
  })
  .refine(
    (schema) => {
      if (schema.firstPeriod && schema.patientBirthDate) {
        return parseInt(schema.firstPeriod) > new Date(schema.patientBirthDate).getFullYear()
      }
      return true
    },
    {
      message: "patient.healthInformation.gynaecology.form.validationMessage.validFirstPeriod",
      path: ["firstPeriod"],
    },
  )

export type GynaecologyForm = z.infer<typeof gynaecologySchema>
